import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Injector,
  input,
  OnInit,
  signal,
  Signal,
} from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { auditTime, filter, Subject, switchMap, tap } from 'rxjs';
import {
  OrderNumber,
  OrderStatusComponent,
  PaymentStatusComponent,
  PublicInvoiceDataSourceService,
  PublicOrderDataSourceService,
  PublicOrderDetails,
  PublicProductNameComponent,
  RefundedPriceComponent,
  RefundedQuantityComponent,
  ShipmentStatusComponent,
} from '@web/shared/module/shop';
import { Divider } from 'primeng/divider';
import {
  AssetImage,
  CurrencyComponent,
  DateComponent,
  DateMonthWithYearComponent,
  DetailsViewComponent,
  DrawerContentComponent,
  DrawerTitleComponent,
  ExternalLinkComponent,
  ImageThumbnailComponent,
  NoDataComponent,
  NumberComponent,
  PrimengButtonDirective,
  PrimengDividerDirective,
  PrimengDrawerDirective,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import {
  Guid,
  isNil,
  isNotNil,
  Nillable,
  once,
  valueOrDefault,
} from '@web/utils';
import {
  Route,
  RouteParam,
  RouterLinkComponent,
  RouterLinkParams,
  scrollToAnchor,
} from '@web/common';
import { ActivatedRoute } from '@angular/router';
import { Drawer } from 'primeng/drawer';
import { PublicOrderShippingAddressEditComponent } from '../public-order-shipping-address-edit/public-order-shipping-address-edit.component';
import { Button } from 'primeng/button';
import { PublicOrderRefundListComponent } from '../public-order-refund-list/public-order-refund-list.component';

@Component({
  selector: 'app-public-order-details',
  imports: [
    Divider,
    PrimengDividerDirective,
    SpinnerFilledComponent,
    DetailsViewComponent,
    CurrencyComponent,
    NoDataComponent,
    ImageThumbnailComponent,
    NgOptimizedImage,
    ShipmentStatusComponent,
    PaymentStatusComponent,
    PublicProductNameComponent,
    OrderStatusComponent,
    DateComponent,
    RouterLinkComponent,
    ExternalLinkComponent,
    Drawer,
    PrimengDrawerDirective,
    DrawerTitleComponent,
    DrawerContentComponent,
    PublicOrderShippingAddressEditComponent,
    Button,
    PrimengButtonDirective,
    DateMonthWithYearComponent,
    RefundedPriceComponent,
    NumberComponent,
    RefundedQuantityComponent,
    PublicOrderRefundListComponent,
  ],
  templateUrl: './public-order-details.component.html',
  styleUrl: './public-order-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PublicOrderDetailsComponent implements OnInit {
  public readonly orderNumber = input.required<OrderNumber>();
  public readonly activatedRoute = input.required<ActivatedRoute>();

  protected readonly params = this.paramsSignal();
  protected readonly data = signal<Nillable<PublicOrderDetails>>(null);
  protected readonly number$ = toObservable(this.orderNumber);
  protected readonly AssetImage = AssetImage;
  protected readonly isNotNil = isNotNil;
  protected readonly Route = Route;
  protected readonly shippingAddressEditVisibility = signal<boolean>(false);
  protected readonly shipmentId = signal<Nillable<Guid>>(null);
  protected readonly isNil = isNil;
  protected readonly valueOrDefault = valueOrDefault;

  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly dataLoaded$ = new Subject<void>();
  private readonly orderDataSource = inject(PublicOrderDataSourceService);
  private readonly invoiceDataSource = inject(PublicInvoiceDataSourceService);

  public ngOnInit(): void {
    this.listenToScrollToAnchorAfterDataLoaded();
    this.fetchData(true);
  }

  public handleDownloadInvoiceClick(): void {
    this.invoiceDataSource.downloadInvoice(this.orderNumber()).subscribe();
  }

  public handleDownloadTemporaryInvoiceClick(): void {
    this.invoiceDataSource
      .downloadTemporaryInvoice(this.orderNumber())
      .subscribe();
  }

  public handleCancelShippingAddressEditEvent(): void {
    this.closeShippingAddressEditDialog();
    this.resolveShipmentId();
  }

  public handleShippingAddressUpdateClick(shipmentId: Guid): void {
    this.shipmentId.set(shipmentId);
    this.showShippingAddressEditDialog();
  }

  public handleSubmitShippingAddressEditEvent(): void {
    this.closeShippingAddressEditDialog();
    this.resolveShipmentId();
    this.fetchData();
  }

  protected readonly customCloseShippingAddressEditEvent = () => {
    this.closeShippingAddressEditDialog();
    this.resolveShipmentId();
  };

  protected resolveShipmentId(): void {
    this.shipmentId.set(null);
  }

  private listenToScrollToAnchorAfterDataLoaded(): void {
    this.dataLoaded$
      .pipe(
        auditTime(0),
        switchMap(() => this.activatedRoute().fragment.pipe(filter(Boolean))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((anchor) => {
        scrollToAnchor(() => ({
          anchor,
          injector: this.injector,
          offset: [0, 200],
        }));
      });
  }

  private fetchData(initial = false): void {
    this.number$
      .pipe(
        once(),
        switchMap((orderNumber) =>
          this.orderDataSource.fetchDetailsByNumber(orderNumber).pipe(
            tap(() => {
              if (initial) {
                this.dataLoaded$.next();
              }
            }),
          ),
        ),
      )
      .subscribe((data) => this.data.set(data));
  }

  private paramsSignal(): Signal<RouterLinkParams> {
    return computed(
      () => ({ [RouteParam.Number]: this.orderNumber() }) as RouterLinkParams,
    );
  }

  private closeShippingAddressEditDialog(): void {
    this.shippingAddressEditVisibility.set(false);
  }

  private showShippingAddressEditDialog(): void {
    this.shippingAddressEditVisibility.set(true);
  }
}
